import {CONFIG} from '../../appConfig';
import STATE from '../../constants/state';

const VEHICLES = 'vehicles/';

/**
 * @typedef {object} Vehicles_List_Data
 * @prop {array} [messages] errors from server
 * @prop {array} vehicle_categories list of vehicle categories
 * @prop {array} vehicle_filters list of vehicle filters data
 * @prop {array} vehicles Array of Vehicles_Data
 */

export default {
  /**
   * @desc Get available vehicle list by country
   * @param {Object} data
   * @returns {object} GMI Service configuration
   * @memberof services.gma.vehicles
   * @see Returned data is {@link Vehicles_List_Data}
   * @example
   * // Service endpoint data
   * const data = {
   *   params: {
   *     cor // *(String)* i.e. 'US'
   *   }
   * }
   * services.gma.vehicles.list(data);
   * // dispatched results stored in `state.gmi.gma.vehicles.by_country.US`
   * // data returned is Vehicles_List_Data
   */
  list(data = {}){
    const {params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${VEHICLES}vehicleList`,
      statePath: STATE.GMA.VEHICLES.BY_COUNTRY.concat([params.cor]),
      params
    };
  },

  /**
   * @desc Get vehicle availability on a location in a given time
   * @param {Object} data
   * @returns {Object}
   * @memberof services.gma.vehicles
   * @example
   * const data = {
   *  payload: {
   *    car_classes,  // *(Array)* i.e. ["ECAR", "PDAR"],
   *    location_pairs: [ // *(Array)*
   *      {
   *        pickup_location_id, // *(String)* "1018967"
   *        return_location_id  // *(String)* "1018967"
   *      }
   *    ],
   *    pickup_time, // *(String)* i.e. "2019-12-13T12:00"
   *    return_time // *(String)* i.e. "2019-12-17T09:00"
   *  }
   * }
   * services.gma.vehicles.availability(data);
   * // this will not store anything to state
   */
  availability(data = {}){
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${VEHICLES}availability`,
      data: payload,
      params
    };
  },

  /**
   * @desc Get available vehicle categories list by country code
   * @param {Object} data
   * @returns {Object} data
   * @memberof services.gma.vehicles
   * @example
   * const data = {
   *   params: {
   *     cor // *(String)* i.e. 'US'
   *   }
   * }
   * services.gma.vehicles.categoriesByCountryCode(data);
   * dispatched results stored in `state.gmi.gma.vehicles.categories.by_country.US`
   */
  categoriesByCountryCode(data = {}){
    const {params = {}} = data;

    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${VEHICLES}categories`,
      statePath: STATE.GMA.VEHICLES.CATEGORIES.BY_COUNTRY.concat([params.cor]),
      params
    };
  },
  /**
   * @desc Get available vehicle categories by countries codes
   * @param {Object} data
   * @returns {Object} data
   * @memberof services.gma.vehicles
   * @example
   * const data = {
   *   params: {
   *     countries // *(String)* i.e. 'US,IE'
   *   },
   * }
   * services.gma.vehicles.categoriesByCountries(data);
   * dispatched results stored in `state.gmi.gma.vehicles.categories.by_countries.vehicles.[US]`
   */
  categoriesByCountries(data = {}){
    const {params = {}} = data;

    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${VEHICLES}categoriesByCountries`,
      statePath: STATE.GMA.VEHICLES.CATEGORIES.BY_COUNTRIES,
      params
    };
  }
};
